// Carte SVG des neuf provinces (tracé vectorisé fourni par le porteur, D-033), deux dimensions
// indépendantes (cadrage, section 6) : suffisance de l'information (gris hachuré si insuffisante, quelle
// que soit la tension) et tension (vert, orange, rouge), avec pastille d'alerte ouverte. Le gris n'est
// jamais une gravité ; une absence de donnée n'est jamais verte (R-28).

import { useId } from 'react';
import { projeterVersPixels } from '@carburanttrack/shared';
import type { CartePixels, ProvinceSituation } from '../api/types';

export interface PointCarte {
  id: string;
  nom: string;
  longitude: number;
  latitude: number;
  categorie: string;
  /** Exception à signaler (retard, position ancienne) : la couleur s'ajoute toujours à un libellé (titre du point). */
  ton?: 'rouge' | 'ambre' | null;
}

/**
 * Mode d'affichage (charte D-038) : « exceptions » laisse le normal en gris neutre et réserve la couleur à ce
 * qui demande une action ; « tout » colore chaque province selon son état (vert compris).
 */
export type ModeCarte = 'exceptions' | 'tout';

export interface ProprietesCarte {
  carte: CartePixels;
  situations?: ProvinceSituation[];
  points?: PointCarte[];
  selection?: string | null;
  surSelection?: (code: string | null) => void;
  pointMisEnAvant?: string | null;
  titre: string;
  legende?: boolean;
  mode?: ModeCarte;
  /** Libellé des points dans la légende (installations par défaut ; missions, livraisons...). */
  libellePoints?: string;
}

export function remplissageProvince(s: ProvinceSituation | undefined, motif: string, mode: ModeCarte = 'tout'): string {
  if (!s || s.information === 'insuffisante' || s.tension === null) return `url(#${motif})`;
  if (s.tension === 'difficulte') return 'var(--rouge)';
  if (s.tension === 'vigilance') return 'var(--ambre)';
  return mode === 'exceptions' ? 'var(--neutre)' : 'var(--vert)';
}

export function descriptionProvince(nom: string, s: ProvinceSituation | undefined): string {
  if (!s) return `${nom} : situation non chargée`;
  const info = s.information === 'insuffisante' ? `information insuffisante (${s.motifInformation})` : 'information suffisante';
  const tension = s.tension === null ? `tension non évaluée (${s.motifTension ?? 'information insuffisante'})` : `tension : ${{ nulle: 'aucune', vigilance: 'vigilance', difficulte: 'difficulté' }[s.tension]}`;
  const alertes = s.alertesOuvertes === null ? 'alertes : indisponible' : `${s.alertesOuvertes} alerte(s) ouverte(s)`;
  return `${nom} : ${info}, ${tension}, ${alertes}`;
}

export function CarteProvinces({ carte, situations = [], points = [], selection, surSelection, pointMisEnAvant, titre, legende = true, mode = 'tout', libellePoints = 'Installation' }: ProprietesCarte) {
  const id = useId().replace(/:/g, '');
  const motif = `hachure-${id}`;
  const [x0, y0, x1, y1] = carte.viewBox;
  const parCode = new Map(situations.map((s) => [s.code, s]));
  const echelle = (x1 - x0) / 300;
  return (
    <div className="carte-provinces">
      <svg viewBox={`${x0} ${y0} ${x1 - x0} ${y1 - y0}`} role="group" aria-label={titre}>
        <defs>
          <pattern id={motif} width="4" height="4" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="4" height="4" fill="var(--hachure-fond)" />
            <rect width="1.2" height="4" fill="var(--hachure-trait)" />
          </pattern>
        </defs>
        {carte.provinces.map((p) => {
          const s = parCode.get(p.code);
          const choisie = selection === p.code;
          return (
            <path
              key={p.code}
              className="province"
              d={p.d}
              fill={remplissageProvince(s, motif, mode)}
              fillOpacity={s?.information === 'suffisante' ? 0.6 : 1}
              tabIndex={surSelection ? 0 : -1}
              role={surSelection ? 'button' : 'img'}
              aria-pressed={surSelection ? choisie : undefined}
              aria-label={descriptionProvince(p.nom, s)}
              onClick={() => surSelection?.(choisie ? null : p.code)}
              onKeyDown={(e) => {
                if (surSelection && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  surSelection(choisie ? null : p.code);
                }
              }}
            >
              <title>{descriptionProvince(p.nom, s)}</title>
            </path>
          );
        })}
        {carte.provinces.map((p) => (
          <text key={`t-${p.code}`} x={p.centroid[0]} y={p.centroid[1] + 2}>
            {p.nom}
          </text>
        ))}
        {carte.provinces.map((p) => {
          const n = parCode.get(p.code)?.alertesOuvertes;
          if (!n) return null;
          return (
            <g key={`a-${p.code}`} aria-label={`${n} alerte(s) ouverte(s) en ${p.nom}`}>
              <circle cx={p.centroid[0] + 12} cy={p.centroid[1] - 8} r={4.5} fill="var(--surface)" stroke="var(--rouge)" strokeWidth={1.6} />
              <text x={p.centroid[0] + 12} y={p.centroid[1] - 5.6} style={{ fontSize: 5.5 }}>
                {n}
              </text>
            </g>
          );
        })}
        {points.map((pt) => {
          const [x, y] = projeterVersPixels([pt.longitude, pt.latitude], carte);
          const avant = pointMisEnAvant === pt.id;
          return (
            <g key={pt.id}>
              {avant && <circle cx={x} cy={y} r={6 * echelle} fill="none" stroke="var(--rouge)" strokeWidth={1.4} />}
              <circle cx={x} cy={y} r={(pt.categorie === 'station' ? 1.6 : 2.6) * echelle} fill={pt.ton === 'rouge' ? 'var(--rouge)' : pt.ton === 'ambre' ? 'var(--ambre)' : 'var(--primaire)'} stroke="var(--surface)" strokeWidth={0.5}>
                <title>{pt.nom}</title>
              </circle>
            </g>
          );
        })}
      </svg>
      {legende && (
        <div className="legende" aria-label="Légende de la carte">
          <div>
            <span className={`echantillon ${mode === 'exceptions' ? 'neutre' : 'vert'}`} />
            {mode === 'exceptions' ? 'Normal' : 'Satisfaisant'}
          </div>
          <div>
            <span className="echantillon ambre" />
            {mode === 'exceptions' ? 'À surveiller' : 'Vigilance'}
          </div>
          <div>
            <span className="echantillon rouge" />
            Difficulté confirmée
          </div>
          <div>
            <span className="echantillon hachure" />
            Information insuffisante (jamais une gravité)
          </div>
          <div>
            <span className="echantillon alerte-ouverte" />
            Alerte ouverte
          </div>
          {points.length > 0 && (
            <div>
              <span className="echantillon installation" />
              {libellePoints} ({points.length} affiché{libellePoints === 'Installation' ? 'e' : ''}{points.length > 1 ? 's' : ''})
            </div>
          )}
        </div>
      )}
    </div>
  );
}
